import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { HiddenPptLogo, RatingWrapper, ChartContainer } from './style';

const D3StarRating = ({
  baseRating,
  newRating,
  totalStars = 5,
  width = 240,
  height = 85,
  starScale = 0.9,     // Configurable star scale size
  starGap = 13,         // Configurable gap to bring stars closer together
}) => {
  const svgRef = useRef(null);
  const instanceIdRef = useRef(`sr-${Math.random().toString(36).slice(2)}`);

  const COLORS = {
    yellowFill: '#E3CC1E',
    orangeFill: '#E97917',
    lightTrack: '#FFE3CA',
    textDark: '#000000',
    textWhite: '#FFFFFF',
    strokeColor: '#E3CC1E',
  };

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const diff = Number((newRating - baseRating).toFixed(2));
    const diffText = `+${diff.toFixed(2)}`;
    const uid = instanceIdRef.current;

    // ----- Outer pill (border only) -----
    const pillX = 0;
    const pillY = 10;
    const outerWidth = 200;
    const outerHeight = 40;
    const outerRadius = outerHeight / 2;

    // ----- Inner pill (the actual colored track) -----
    const padding = 3;
    const innerX = pillX + padding;
    const innerY = pillY + padding;
    const innerWidth = outerWidth - padding * 2;
    const innerHeight = outerHeight - padding * 2;
    const innerRadius = innerHeight / 2;

    const baseWidth = (baseRating / totalStars) * innerWidth;
    const newWidth = (newRating / totalStars) * innerWidth;

    const defs = svg.append('defs');

    defs.append('clipPath')
      .attr('id', `inner-clip-${uid}`)
      .append('rect')
      .attr('x', innerX)
      .attr('y', innerY)
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('rx', innerRadius);

    const fillGroup = svg.append('g').attr('clip-path', `url(#inner-clip-${uid})`);

    fillGroup.append('rect')
      .attr('x', innerX)
      .attr('y', innerY)
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', COLORS.lightTrack);

    fillGroup.append('rect')
      .attr('x', innerX)
      .attr('y', innerY)
      .attr('width', baseWidth)
      .attr('height', innerHeight)
      .attr('fill', COLORS.yellowFill);

    fillGroup.append('rect')
      .attr('x', innerX + baseWidth)
      .attr('y', innerY)
      .attr('width', Math.max(newWidth - baseWidth, 0))
      .attr('height', innerHeight)
      .attr('fill', COLORS.orangeFill);

    svg.append('rect')
      .attr('x', pillX)
      .attr('y', pillY)
      .attr('width', outerWidth)
      .attr('height', outerHeight)
      .attr('rx', outerRadius)
      .attr('fill', 'none')
      .attr('stroke', COLORS.strokeColor)
      .attr('stroke-width', 1);

    // ----- Stars: configurable sizing and compact spacing -----
    const starPath = 'M8.41547 21.1185L13.6155 18.031L18.8561 21.1185L17.4748 15.2685L21.9842 11.4091L16.0123 10.881L13.6155 5.31538L11.2186 10.881L5.24672 11.4091L9.79672 15.2685L8.41547 21.1185ZM5.20609 25.4841L7.45306 16.0684L0 9.73416L9.77478 8.88428L13.6155 0L17.4562 8.92491L27.2309 9.73416L19.7779 16.0684L22.0248 25.4841L13.6155 20.4904L5.20609 25.4841Z';
    
    const STAR_W = 27.2309 * starScale;
    const STAR_H = 25.4841 * starScale;

    // Calculate total block width of stars + custom gaps to center them nicely
    const totalStarsWidth = (totalStars * STAR_W) + ((totalStars - 1) * starGap);
    const starsStartX = innerX + (innerWidth - totalStarsWidth) / 2;

    const starsGroup = svg.append('g').attr('transform', `translate(0, ${innerY + (innerHeight - STAR_H) / 2})`);

    for (let i = 0; i < totalStars; i++) {
      const slotOffset = starsStartX + i * (STAR_W + starGap);
      starsGroup.append('path')
        .attr('d', starPath)
        .attr('transform', `translate(${slotOffset}, 0) scale(${starScale})`)
        .attr('fill', '#ffffff');
    }

    // ----- Divider lines -----
    const baseX = innerX + baseWidth;
    const newX = innerX + newWidth;
    const lineYStart = pillY - 12;
    const lineYEnd = pillY + outerHeight + 4;

    svg.append('line')
      .attr('x1', baseX)
      .attr('y1', lineYStart + 7)
      .attr('x2', baseX)
      .attr('y2', lineYEnd)
      .attr('class', 'lineLeft')
      .attr('stroke', COLORS.textDark)
      .attr('stroke-width', 1.0);

    svg.append('line')
      .attr('x1', newX)
      .attr('y1', lineYStart + 7)
      .attr('x2', newX)
      .attr('y2', lineYEnd)
      .attr('class', 'lineRight')
      .attr('stroke', COLORS.textDark)
      .attr('stroke-width', 1.0);

    svg.append('text')
      .attr('x', (baseX + newX) / 2)
      .attr('y', lineYStart + 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('fill', COLORS.textDark)
      .text(diffText);

    // ----- Value badges (height: 15px, total width: 40px including circular end-cap) -----
    const badgeY = pillY + outerHeight + 3;
    const badgeHeight = 15;
    const badgeRadius = badgeHeight / 2;
    // Total width requested is 40px, and the end-cap circle has a radius of badgeRadius.
    // Therefore, the middle rectangle width must be: 40 - badgeRadius = 40 - 7.5 = 32.5px.
    const badgeWidth = 40 - badgeRadius - 10;
    const textGap = 3;

    const baseBadge = svg.append('g')
      .attr('transform', `translate(${baseX - badgeWidth}, ${badgeY})`);
    baseBadge.append('rect')
      .attr('width', badgeWidth)
      .attr('height', badgeHeight)
      .attr('fill', COLORS.yellowFill);
    baseBadge.append('circle')
      .attr('cx', 0)
      .attr('cy', badgeRadius)
      .attr('r', badgeRadius)
      .attr('fill', COLORS.yellowFill);
    baseBadge.append('text')
      .attr('x', badgeWidth / 2 - textGap)
      .attr('y', badgeHeight / 2 + 3.5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', '400')
      .attr('fill', COLORS.textDark)
      .text(baseRating.toFixed(2));

    const newBadge = svg.append('g')
      .attr('transform', `translate(${newX}, ${badgeY})`);
    newBadge.append('rect')
      .attr('width', badgeWidth)
      .attr('height', badgeHeight)
      .attr('fill', COLORS.orangeFill);
    newBadge.append('circle')
      .attr('cx', badgeWidth)
      .attr('cy', badgeRadius)
      .attr('r', badgeRadius)
      .attr('fill', COLORS.orangeFill);
    newBadge.append('text')
      .attr('x', badgeWidth / 2 + textGap)
      .attr('y', badgeHeight / 2 + 3.5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', '400')
      .attr('fill', COLORS.textWhite)
      .text(newRating.toFixed(2));


  }, [baseRating, newRating, totalStars, starScale, starGap]);

  return (
    <ChartContainer>
      <svg ref={svgRef} width={width-20} height={height} style={{marginTop:'20px'}} />
    </ChartContainer>
  );
};

export default D3StarRating;